/**
 * Contact Form 7 — SPA reinit shim + client-side required guard.
 *
 * CF7 binds its submit / validate / reset handlers inside a
 * `DOMContentLoaded` listener that fires only on hard navigation. After
 * a Barba transition the new container has fresh `<form class="wpcf7-form">`
 * markup but no JS bindings, so submitting silently full-page-loads
 * (or, worse, posts to wp-comments-post.php-style URLs depending on the
 * form action).
 *
 * `handleCf7()` is idempotent and re-entrant — call it from `initApp()`
 * after every page load. Forms already initialized (have `form.wpcf7`)
 * are skipped. Pre-existing `.wpcf7-spinner` markers from server-rendered
 * HTML are dropped before the official init runs to avoid spinner
 * duplication, since `wpcf7.init()` always inserts its own spinner span
 * after every `.has-spinner` button.
 *
 * On top of that we add a capture-phase required guard: submitting with an
 * empty required field otherwise sends the request to the server (the spinner
 * spins) just to get the "field is required" message back. The guard blocks the
 * submit before CF7's own handler runs and paints CF7's native invalid markup
 * inline, so the message shows instantly with no round-trip.
 */

const REQUIRED_MESSAGE = 'The field is required.';

/** Does a required control currently hold no value? */
const isEmptyControl = (field) => {
	const type = (field.type || '').toLowerCase();

	if (type === 'checkbox' || type === 'radio') {
		// Group emptiness: no member of the same name is checked.
		const group = field.form
			? field.form.querySelectorAll(`input[name="${CSS.escape(field.name)}"]`)
			: [field];
		return ![...group].some((el) => el.checked);
	}

	if (field.multiple && field.selectedOptions) {
		return field.selectedOptions.length === 0;
	}

	return String(field.value || '').trim() === '';
};

/** The wrapper CF7 uses to host a field's not-valid tip. */
const wrapFor = (field) =>
	field.closest('.wpcf7-form-control-wrap') || field.parentElement || field;

/** Paint CF7's native invalid state + tip under a field. */
const markInvalid = (field) => {
	field.classList.add('wpcf7-not-valid');
	field.setAttribute('aria-invalid', 'true');

	const wrap = wrapFor(field);
	wrap.querySelectorAll(':scope > .wpcf7-not-valid-tip').forEach((tip) => tip.remove());

	const tip = document.createElement('span');
	tip.className = 'wpcf7-not-valid-tip';
	tip.setAttribute('aria-hidden', 'true');
	tip.textContent = REQUIRED_MESSAGE;
	wrap.appendChild(tip);
};

/** Clear the invalid state once the field has a value again. */
const clearInvalid = (field) => {
	field.classList.remove('wpcf7-not-valid');
	field.removeAttribute('aria-invalid');
	wrapFor(field)
		.querySelectorAll(':scope > .wpcf7-not-valid-tip')
		.forEach((tip) => tip.remove());
};

const wireRequiredGuard = (form) => {
	if (form.dataset.jkdcf7Guard === '1') return;
	form.dataset.jkdcf7Guard = '1';

	form.addEventListener(
		'submit',
		(event) => {
			const requireds = form.querySelectorAll('[aria-required="true"], [required]');
			let firstInvalid = null;

			requireds.forEach((field) => {
				if (isEmptyControl(field)) {
					markInvalid(field);
					if (!firstInvalid) firstInvalid = field;
				}
			});

			if (firstInvalid) {
				// Stop CF7's own submit handler (bubble phase) and the native
				// submit, so no request fires and the spinner never spins.
				event.preventDefault();
				event.stopImmediatePropagation();
				firstInvalid.focus();
			}
		},
		true,
	);

	// Drop the inline error as soon as the visitor gives the field a value.
	form.addEventListener('input', (event) => {
		const field = event.target;
		if (
			field.matches?.('[aria-required="true"], [required]') &&
			!isEmptyControl(field)
		) {
			clearInvalid(field);
		}
	});
	form.addEventListener('change', (event) => {
		const field = event.target;
		if (
			field.matches?.('[aria-required="true"], [required]') &&
			!isEmptyControl(field)
		) {
			clearInvalid(field);
		}
	});
};

export const handleCf7 = (root = document) => {
	const wpcf7 = window.wpcf7;
	if (!wpcf7 || typeof wpcf7.init !== 'function') return;

	const forms = root.querySelectorAll('.wpcf7 > form, form.wpcf7-form');
	if (!forms.length) return;

	forms.forEach((form) => {
		if (form.wpcf7) {
			wireRequiredGuard(form);
			return;
		}

		form
			.querySelectorAll('.has-spinner ~ .wpcf7-spinner, .wpcf7-spinner + .wpcf7-spinner')
			.forEach((node) => node.remove());

		try {
			wpcf7.init(form);
		} catch (err) {
			console.warn('[cf7] init failed', err);
			return;
		}

		wireRequiredGuard(form);

		const wrapper = form.closest('.wpcf7');
		if (wrapper) wrapper.classList.replace('no-js', 'js');
	});
};
